import { FC, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { Box, Container, HStack, IconButton, Image, rem, useUserColorMode, XOutline } from '@bonusly/design-system';

import darkLogo from '../../assets/images/bonusly-logo-dark-mode.png';
import logo from '../../assets/images/bonusly-logo.png';
import { PageLoading } from '../loading';
import { useCloseWindow } from './hooks/use-close-window';

const PublicLayoutHeader: FC = () => {
	const { requestClose } = useCloseWindow();
	const { t } = useTranslation('recognition', { keyPrefix: 'externalRecognition.publicForm' });
	const { isDarkMode } = useUserColorMode();

	return (
		<Box
			bg="bg.surface"
			borderBottomColor="border.default"
			borderBottomStyle="solid"
			borderBottomWidth="1px"
			position="sticky"
			top="0"
			w="100%"
			zIndex="docked"
		>
			<Container maxW="100%" px={4}>
				<HStack align="center" h={16} justify="space-between" w="full">
					<Box h={rem(46)} maxW={72}>
						<Image alt="Bonusly" h="full" objectFit="contain" src={isDarkMode ? darkLogo : logo} />
					</Box>
					<IconButton
						aria-label={t('closeButton')}
						bg="secondary.bgAlpha"
						borderRadius="full"
						icon={<XOutline />}
						onClick={requestClose}
						size="md"
						_hover={{ bg: 'secondary.bgAlphaHover' }}
					/>
				</HStack>
			</Container>
		</Box>
	);
};

// Session-free shell for the unauthenticated public form. Cloned from
// NavlessLayout's visual structure (sticky logo header + centered content) but
// with EVERY identity hook stripped — no useCurrentCompany, no useCurrentUser,
// no spoofing/seasonal logo. It renders for an anonymous visitor with an empty
// identity store, so it must not read any user/company state. The Bonusly logo
// is used (light or dark variant based on color mode) because there is no
// company context to source a custom logo from.
export const PublicLayout: FC = () => {
	return (
		<Box bg="bg.surface" display="flex" flexDirection="column" minH="100vh" minW={rem(370)}>
			<PublicLayoutHeader />

			<Box display="flex" flexDirection="column" flexGrow={1} pos="relative" zIndex="base">
				<Suspense fallback={<PageLoading />}>
					<Outlet />
				</Suspense>
			</Box>
		</Box>
	);
};
